import { isEmpty } from ".";
import { isNumber } from "./number";

/**
 * Converts string to camel case.
 * @param {String} str
 * @returns {String}
 */
export const toCamel = (str) =>
  str
    .toLowerCase()
    .replace(/ /g, "_")
    .replace(/([-_][a-z])/gi, ($1) =>
      $1.toUpperCase().replace("-", "").replace("_", "")
    );

/**
 * Converts string to camel case.
 * @param {String} str
 * @returns {String}
 */
export const toCamel2 = (str) => {
  str = str.replace(/[-_\s.]+(.)?/g, (_, c) => (c ? c.toUpperCase() : ""));
  return str.substring(0, 1).toLowerCase() + str.substring(1);
};

/**
 * Converts string to snake case.
 * @param {String} str
 * @returns {String}
 */
export const toSnake = (string) => {
  return string
    .replace(/\W+/g, " ")
    .split(/ |\B(?=[A-Z])/)
    .map((word) => word.toLowerCase())
    .join("_");
};

/**
 * Capitalizes every word in a string.
 * @param {String} str
 * @returns {String}
 */
export const ucwords = (str) => {
  return (str + "").replace(/^([a-z])|\s+([a-z])/g, function ($1) {
    return $1.toUpperCase();
  });
};

/**
 * Capitalizes the first character in a string.
 * @param {String} str
 * @returns {String}
 */
export const capitalize = (str) => str && str[0].toUpperCase() + str.slice(1);

/**
 * Pads number with zeros.
 * @param {Number|String} num
 * @param {Number} places
 * @returns {String}
 */
export const zeroPad = (num, places) => String(num).padStart(places, "0");

/**
 * Removes non-numeric characters.
 * @param {Number|String} value
 * @returns {String}
 */
export const parseInteger = (value) =>
  (value ?? "0").trim().replace(/[^\d-]/g, "");

/**
 * Removes non-numeric characters.
 * @param {Number|String} value
 * @returns {String}
 */
export const parseMoney = (value) =>
  (value ?? "0.00").trim().replace(/[^\d.-]/g, "");

/**
 * Removes non-numeric characters.
 * @param {Number|String} value
 * @returns {String}
 */
export const parseDecimal = (value) =>
  (value ?? "0.00").trim().replace(/[^\d.-]/g, "");

/**
 * Removes a character from the begining of a string.
 * @param {String} str
 * @param {Strin} char
 * @returns {String}
 */
export const ltrimChar = (str, char) =>
  str.charAt(0) === char ? str.substring(1, str.length) : str;

/**
 * Removes a character from the end of a string.
 * @param {String} str
 * @param {String} char
 * @returns {String}
 */
export const rtrimChar = (str, char) =>
  str.charAt(str.length - 1) === char ? str.substring(0, str.length - 1) : str;

/**
 * Replaces all instances of search with replace.
 * @param {String} string
 * @param {String} search
 * @param {String}  replace
 * @returns {String}
 */
export const replaceAll = (string, search, replace) =>
  string.split(search).join(replace);

/**
 * Formats a string using placeholders and an object of substitutions.
 * @param {String} value
 * @param {{String: String}} replacements
 * @returns
 */
export const substitutions = (value, replacements) => {
  return value.replace(
    /{(\w+)}/g,
    (placeholderWithDelimiters, placeholderWithoutDelimiters) =>
      replacements.hasOwnProperty(placeholderWithoutDelimiters)
        ? replacements[placeholderWithoutDelimiters]
        : placeholderWithDelimiters
  );
};

/**
 * Removes non-numeric characters.
 * @param {String} value
 * @returns {String|null}
 */
export const parseZipCode = (value) => {
  if (/(^\d{5}$)|(^\d{5}-\d{4}$)/.test(value)) {
    return value;
  }

  return null;
};

/**
 * Removes non-numeric characters.
 * @param {String} value
 * @returns {String|null}
 */
export const parsePhoneNumber = (value) => {
  let newValue = `${value}`;
  newValue = newValue ? newValue.replace(/\D/g, "") : null;
  if (!newValue) {
    return null;
  }

  const firstChar = newValue.charAt(0);
  if (firstChar === "1") {
    if (newValue.length === 11) {
      return newValue.substring(1, newValue.length);
    }

    return null;
  }

  if (newValue.length === 10) {
    return newValue;
  }

  return null;
};

/**
 * Returns the value if a valid email address.
 * @param {String} value
 * @returns {String|null}
 */
export const parseEmailAddress = (value) =>
  value && value.indexOf("@") > -1 ? value : null;

/**
 * Parses value to boolean.
 * @param {Boolean|Number|String} value
 * @returns {Boolean}
 */
export const parseBoolean = (value) => {
  const type = typeof value;

  switch (type) {
    case "boolean":
      return value;
    case "string":
      if (value === "0" || value.toLowerCase() === "false") {
        return false;
      }
      if (value === "1" || value.toLowerCase() === "true") {
        return true;
      }
      return false;
    case "number":
      if (value === 0) {
        return false;
      }
      return true;
    default:
      return false;
  }
};

/**
 * Source https://stackoverflow.com/questions/105034/how-do-i-create-a-guid-uuid
 * @returns {String}
 */
export const uuidv4 = () => {
  let d = new Date().getTime(),
    d2 =
      (typeof performance !== "undefined" &&
        performance.now &&
        performance.now() * 1000) ||
      0;
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    let r = Math.random() * 16;
    if (d > 0) {
      r = (d + r) % 16 | 0;
      d = Math.floor(d / 16);
    } else {
      r = (d2 + r) % 16 | 0;
      d2 = Math.floor(d2 / 16);
    }
    return (c === "x" ? r : (r & 0x7) | 0x8).toString(16);
  });
};

/**
 * Checks the specified value's length is greater or equal than
 * the specified length.
 *
 * @param {String} value
 * @param {Number} minLength
 * @returns {Boolean}
 */
export const isMinLength = (value, minLength) => value.length >= minLength;

/**
 * Checks the specified value's is less or equal than
 * the specified length.
 *
 * @param {String} value
 * @param {Number} maxLength
 * @returns {Boolean}
 */
export const isMaxLength = (value, maxLength) => value.length <= maxLength;

/**
 * Checks to see if string has at least n amount of numbers
 * @param {String} value
 * @param {Integer} number
 * @returns {Boolean}
 */
export const hasNumbers = (value, number) => {
  if (isEmpty(value)) {
    return false;
  }

  let count = 0;
  [...value].forEach((char) => {
    if (isNumber(char)) {
      count++;
    }
  });
  return count >= number;
};
